// GameplayPage.tsx — Section V: Gameplay.
//
// "Tables, modes, completion. What is happening on the felt right now."
// (Lore-approved GP-03)
//
// Wire: game_canister.admin_get_metrics() via admin_get_aggregated_metrics().
// Lifetime counters return 0 per K1 — shown with "Lifetime tracking begins post-launch" caption.
// Empty state: "No games in progress. Tables are open."

import React from 'react';
import type { Identity } from '@dfinity/agent';
import MetricCard from '../components/MetricCard';
import { useAdminMetrics } from '../hooks/useAdminMetrics';

interface GameplayPageProps {
  identity: Identity | null;
}

function formatNat(n: bigint): string {
  return Number(n).toLocaleString('en-US');
}

function formatMs(ms: bigint): string {
  const minutes = Math.floor(Number(ms) / 60_000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem > 0 ? `${hours}h ${rem}m` : `${hours}h`;
}

export default function GameplayPage({ identity }: GameplayPageProps): React.ReactElement {
  const { metrics, isLoading, lastFetchedAt, refresh } = useAdminMetrics(identity);

  const game = metrics?.game?.[0] ?? null;
  const hasData = game !== null;

  const gamesInProgress = game ? formatNat(game.gamesInProgress) : '0';
  const popularTarget = game ? `${Number(game.mostPopularTarget)}` : '—';
  const popularMode = game
    ? (game.mostPopularMode === 'draw' ? 'Draw' : game.mostPopularMode === 'straight' ? 'Straight' : game.mostPopularMode)
    : '—';
  const aiRatio = game ? `${Number(game.aiVsMultiplayerRatio)}%` : '—';
  const avgDuration = game ? formatMs(BigInt(game.avgGameDurationMs)) : '—';

  return (
    <section className="section" id="gameplay" aria-labelledby="gameplay-title">
      <header className="section-header">
        <span className="section-eyebrow">Section V</span>
        <h2 className="section-title" id="gameplay-title">Gameplay</h2>
        <p className="section-lede">
          Tables, modes, completion. What is happening on the felt right now.
        </p>
        {lastFetchedAt && (
          <p className="section-meta">
            Last refreshed · {lastFetchedAt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })} UTC
            {' '}
            <button className="btn-inline" onClick={refresh} disabled={isLoading}>
              {isLoading ? 'Refreshing' : 'Refresh'}
            </button>
          </p>
        )}
      </header>

      {Number(gamesInProgress) === 0 && !isLoading && !hasData ? (
        <div className="empty-state">
          <p className="empty-state-text">No games in progress. Tables are open.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-4" style={{ marginBottom: 'var(--s-lg)' }}>
            <MetricCard
              label="Games In Progress"
              numeral={gamesInProgress}
              caption="Tables with a human seated."
              trend="Live"
              trendDir="flat"
            />
            <MetricCard
              label="Games Today"
              numeral={game ? formatNat(game.gamesCompletedLast24h) : '0'}
              caption="Started since 00:00 UTC."
            />
            <MetricCard
              label="Games Completed / 7d"
              numeral={game ? formatNat(game.gamesCompletedLast7d) : '0'}
              caption="Reached a winner or block."
            />
            <MetricCard
              label="Average Match Length"
              numeral={avgDuration}
              caption="First tile to win banner."
            />
          </div>

          {/* Mode Mix + Composition panels */}
          <div className="grid grid-2">
            <div className="panel">
              <p className="panel-title">Mode Mix · 7 days</p>
              <p className="panel-subtitle">Share of completed games by ruleset.</p>
              {game ? (
                <ul className="source-list">
                  <li className="source-row">
                    <span className="source-name">Draw — 5 tiles + boneyard</span>
                    <span className="source-value">{popularMode === 'Draw' ? '—' : '—'}</span>
                    <div className="source-bar" style={{ '--w': '60%' } as React.CSSProperties} />
                  </li>
                  <li className="source-row">
                    <span className="source-name">Straight — 7 tiles, no boneyard</span>
                    <span className="source-value">—</span>
                    <div className="source-bar" style={{ '--w': '40%' } as React.CSSProperties} />
                  </li>
                </ul>
              ) : (
                <p className="card-caption">
                  Lifetime tracking begins post-launch.
                </p>
              )}
              <p className="panel-subtitle" style={{ marginTop: 'var(--s-md)', marginBottom: 0 }}>
                Most popular target: {popularTarget} points.
              </p>
            </div>

            <div className="panel">
              <p className="panel-title">Composition · 7 days</p>
              <p className="panel-subtitle">Multiplayer share versus AI-only tables.</p>
              {game ? (
                <ul className="source-list">
                  <li className="source-row">
                    <span className="source-name">Multiplayer (4 humans)</span>
                    <span className="source-value">{aiRatio}</span>
                    <div className="source-bar" style={{ '--w': `${100 - Number(game.aiVsMultiplayerRatio)}%` } as React.CSSProperties} />
                  </li>
                  <li className="source-row">
                    <span className="source-name">Mixed (humans + AI seats)</span>
                    <span className="source-value">—</span>
                    <div className="source-bar" style={{ '--w': '20%' } as React.CSSProperties} />
                  </li>
                  <li className="source-row">
                    <span className="source-name">Solo vs AI (offline)</span>
                    <span className="source-value">—</span>
                    <div className="source-bar" style={{ '--w': '10%' } as React.CSSProperties} />
                  </li>
                </ul>
              ) : (
                <p className="card-caption">Lifetime tracking begins post-launch.</p>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
}
